import * as exConstants from './constants';

// ----------------------------------------------------------------------------

type TypeConfig = {
    sidebarBreakpoint: number;
};

const DefaultsConfig: TypeConfig = {
    sidebarBreakpoint: 992,
};

// ----------------------------------------------------------------------------

class PushMenuManager {
    private readonly _element: HTMLElement;

    private _config: TypeConfig;

    constructor(element: HTMLElement, config?: Partial<TypeConfig>) {
        this._element = element;
        this._config = { ...DefaultsConfig, ...config };
    }

    addSidebarBreakPoint(): void {
        this._config = {
            ...this._config,
            sidebarBreakpoint: this.resolveSidebarBreakpoint(),
        };

        this.syncSidebarState();
    }

    collapse(): void {
        const event = new Event(exConstants.EVENT_COLLAPSE);

        document.body.classList.remove(exConstants.CLASS_NAME_SIDEBAR_OPEN);
        document.body.classList.add(exConstants.CLASS_NAME_SIDEBAR_COLLAPSE);

        this._element.dispatchEvent(event);
    }

    expand(): void {
        const event = new Event(exConstants.EVENT_OPEN);

        document.body.classList.remove(exConstants.CLASS_NAME_SIDEBAR_COLLAPSE);
        document.body.classList.add(exConstants.CLASS_NAME_SIDEBAR_OPEN);

        this._element.dispatchEvent(event);
    }

    init(): void {
        this.addSidebarBreakPoint();
    }

    menusClose(): void {
        const rootElement = this._element.closest(exConstants.SELECTOR_APP_SIDEBAR) ?? document;
        const navTreeview = rootElement.querySelectorAll<HTMLElement>(exConstants.SELECTOR_NAV_TREEVIEW);

        navTreeview.forEach((navTree): any => {
            navTree.style.removeProperty('display');
            navTree.style.removeProperty('height');
        });

        const navSidebar =
            rootElement instanceof Element && rootElement.matches(exConstants.SELECTOR_SIDEBAR_MENU)
                ? rootElement
                : rootElement.querySelector<HTMLElement>(exConstants.SELECTOR_SIDEBAR_MENU);
        const navItem = navSidebar?.querySelectorAll<HTMLElement>(exConstants.SELECTOR_NAV_ITEM);

        navItem?.forEach((item): any => {
            item.classList.remove(exConstants.CLASS_NAME_MENU_OPEN);
        });
    }

    toggle(): void {
        if (document.body.classList.contains(exConstants.CLASS_NAME_SIDEBAR_COLLAPSE)) {
            this.expand();
        } else {
            this.collapse();
        }
    }

    private getSidebarExpandClass(): string | null {
        const sidebarExpandList = document.querySelector(exConstants.SELECTOR_SIDEBAR_EXPAND)?.classList;

        if (!sidebarExpandList) {
            return null;
        }

        return (
            Array.from(sidebarExpandList).find((className): any =>
                className.startsWith(exConstants.CLASS_NAME_SIDEBAR_EXPAND),
            ) ?? null
        );
    }

    private resolveSidebarBreakpoint(): number {
        const sidebarExpandClass = this.getSidebarExpandClass();

        if (!sidebarExpandClass) {
            return this._config.sidebarBreakpoint;
        }

        const sidebar = document.getElementsByClassName(sidebarExpandClass)[0];

        if (!(sidebar instanceof Element)) {
            return this._config.sidebarBreakpoint;
        }

        const sidebarContent = window.getComputedStyle(sidebar, '::before').getPropertyValue('content');
        const nextBreakpoint = Number(sidebarContent.replace(/[^\d.-]/g, ''));

        return Number.isFinite(nextBreakpoint) ? nextBreakpoint : this._config.sidebarBreakpoint;
    }

    private syncSidebarState(): void {
        const bodyClassList = document.body.classList;

        if (window.innerWidth <= this._config.sidebarBreakpoint) {
            this.collapse();

            return;
        }

        if (!bodyClassList.contains(exConstants.CLASS_NAME_SIDEBAR_MINI)) {
            this.expand();

            return;
        }

        if (bodyClassList.contains(exConstants.CLASS_NAME_SIDEBAR_COLLAPSE)) {
            this.collapse();
        }
    }
}

// ----------------------------------------------------------------------------

export default PushMenuManager;
